// src/pages/Finance/ReminderHistory/ReminderHistory.jsx
import React, { useState, useEffect, useMemo } from "react"
import {
  Row,
  Col,
  Table,
  Typography,
  Tag,
  Space,
  DatePicker,
  Select,
  Input,
  Button,
  Tooltip,
  message,
  Breadcrumb,
} from "antd"
import {
  MailOutlined,
  FilePdfOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  WarningOutlined,
  HomeOutlined,
} from "@ant-design/icons"
import { collection, query, orderBy, onSnapshot } from "firebase/firestore"
import { useNavigate } from "react-router-dom"
import dayjs from "dayjs"
import { db } from "../../../../firebase/config"
import PageHeader from "../../../../components/Elements/PageHeader"

const { Text, Link } = Typography
const { RangePicker } = DatePicker

export default function ReminderHistory() {
  const navigate = useNavigate()
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)

  // ── Filters ────────────────────────────────────────────────────────────────
  const [dateRange, setDateRange] = useState(null)
  const [clientSearch, setClientSearch] = useState("")
  const [companyFilter, setCompanyFilter] = useState(null)

  // ── Fetch all reminder logs ────────────────────────────────────────────────
  useEffect(() => {
    const q = query(collection(db, "reminderLogs"), orderBy("sentAt", "desc"))

    const unsub = onSnapshot(
      q,
      (snap) => {
        setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
        setLoading(false)
      },
      (err) => {
        console.error("Error fetching reminder logs:", err)
        message.error("Failed to load reminder history.")
        setLoading(false)
      },
    )

    return () => unsub()
  }, [])

  // ── Derived filter options ─────────────────────────────────────────────────
  const companyOptions = useMemo(() => {
    const names = new Set(logs.map((l) => l.companyName).filter(Boolean))
    return Array.from(names)
      .sort()
      .map((n) => ({ label: n, value: n }))
  }, [logs])

  // ── Apply filters ──────────────────────────────────────────────────────────
  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      // Client name search
      if (clientSearch) {
        const search = clientSearch.toLowerCase()
        if (!(log.clientName || "").toLowerCase().includes(search)) return false
      }

      // Company filter
      if (companyFilter && log.companyName !== companyFilter) return false

      // Date range
      if (dateRange?.[0] && dateRange?.[1]) {
        const sentAt = log.sentAt?.seconds ? dayjs.unix(log.sentAt.seconds) : null
        if (!sentAt) return false
        if (sentAt.isBefore(dateRange[0].startOf("day"))) return false
        if (sentAt.isAfter(dateRange[1].endOf("day"))) return false
      }

      return true
    })
  }, [logs, clientSearch, companyFilter, dateRange])

  // ── Helpers ────────────────────────────────────────────────────────────────
  const formatTs = (ts) => {
    if (!ts?.seconds) return "—"
    return dayjs.unix(ts.seconds).format("DD.MM.YYYY HH:mm:ss")
  }

  const money = (n = 0) =>
    Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  // ── Columns ────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Sent At",
      dataIndex: "sentAt",
      key: "sentAt",
      width: 150,
      render: (ts) => <Text style={{ fontSize: 12 }}>{formatTs(ts)}</Text>,
      sorter: (a, b) => (a.sentAt?.seconds || 0) - (b.sentAt?.seconds || 0),
      defaultSortOrder: "descend",
    },
    {
      title: "Client",
      dataIndex: "clientName",
      key: "clientName",
      ellipsis: true,
      render: (name) => <Text strong>{name || "—"}</Text>,
    },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
      width: 150,
      ellipsis: true,
      render: (name) => <Text>{name || "—"}</Text>,
    },
    {
      title: "Sent To",
      dataIndex: "sentTo",
      key: "sentTo",
      width: 200,
      render: (emails) => (
        <Space direction="vertical" size={2}>
          {(emails || []).map((email) => (
            <Tag key={email} icon={<MailOutlined />} color="blue" style={{ fontSize: 11 }}>
              {email}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Invoices",
      dataIndex: "invoiceCount",
      key: "invoiceCount",
      align: "center",
      width: 75,
      render: (count) => <Tag color="default">{count || "—"}</Tag>,
    },
    {
      title: "Total (EUR)",
      dataIndex: "totalSum",
      key: "totalSum",
      align: "right",
      width: 100,
      render: (val) => <Text style={{ color: "#f5222d" }}>{val ? money(val) : "—"}</Text>,
    },
    {
      title: "Status",
      dataIndex: "error",
      key: "status",
      align: "center",
      width: 80,
      render: (error) =>
        error ? (
          <Tooltip title={error}>
            <Tag icon={<CloseCircleOutlined />} color="error">
              Failed
            </Tag>
          </Tooltip>
        ) : (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Sent
          </Tag>
        ),
      filters: [
        { text: "Sent", value: false },
        { text: "Failed", value: true },
      ],
      onFilter: (value, record) => !!record.error === value,
    },
    {
      title: "PDF",
      dataIndex: "storageUrl",
      key: "pdf",
      align: "center",
      width: 55,
      render: (url) =>
        url ? (
          <Tooltip title="Open PDF">
            <Link href={url} target="_blank" rel="noopener noreferrer">
              <FilePdfOutlined style={{ fontSize: 16, color: "#f5222d" }} />
            </Link>
          </Tooltip>
        ) : (
          <Text type="secondary" style={{ fontSize: 11 }}>
            —
          </Text>
        ),
    },
    {
      title: "Sent By",
      dataIndex: "sentBy",
      key: "sentBy",
      width: 110,
      ellipsis: true,
      render: (uid) => (
        <Text type="secondary" style={{ fontSize: 11 }}>
          {uid || "—"}
        </Text>
      ),
    },
  ]

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <Breadcrumb
            style={{ marginBottom: 8 }}
            items={[
              {
                title: (
                  <span style={{ cursor: "pointer" }} onClick={() => navigate("/")}>
                    <HomeOutlined /> Home
                  </span>
                ),
              },
              {
                title: (
                  <span style={{ cursor: "pointer" }} onClick={() => navigate(-1)}>
                    <WarningOutlined /> Overdue Payments
                  </span>
                ),
              },
              {
                title: (
                  <span>
                    <MailOutlined /> Reminder History
                  </span>
                ),
              },
            ]}
          />
          <PageHeader text="Reminder Email History" icon={<MailOutlined />} />
        </Col>

        {/* Filters */}
        <Col span={24}>
          <div style={{ background: "white", borderRadius: 15, padding: 20 }}>
            <Space wrap size={12}>
              <Input
                placeholder="Search client..."
                prefix={<SearchOutlined />}
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                allowClear
                style={{ width: 200 }}
              />
              <Select
                placeholder="Filter by company"
                options={companyOptions}
                value={companyFilter}
                onChange={setCompanyFilter}
                allowClear
                style={{ width: 200 }}
              />
              <RangePicker value={dateRange} onChange={setDateRange} format="DD.MM.YYYY" />
              <Button
                onClick={() => {
                  setClientSearch("")
                  setCompanyFilter(null)
                  setDateRange(null)
                }}>
                Clear Filters
              </Button>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {filteredLogs.length} record{filteredLogs.length !== 1 ? "s" : ""}
              </Text>
            </Space>
          </div>
        </Col>

        {/* Table */}
        <Col span={24}>
          <div style={{ background: "white", borderRadius: 15, padding: 20 }}>
            <Table
              size="small"
              rowKey="id"
              columns={columns}
              dataSource={filteredLogs}
              loading={loading}
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} total` }}
              bordered
              expandable={{
                expandedRowRender: (record) => (
                  <div style={{ padding: "8px 16px" }}>
                    <Space direction="vertical" size={4}>
                      {record.invoiceIds?.length > 0 && (
                        <Text style={{ fontSize: 12 }}>
                          <Text strong>Invoice IDs: </Text>
                          {record.invoiceIds.join(", ")}
                        </Text>
                      )}
                      {record.error && (
                        <Text type="danger" style={{ fontSize: 12 }}>
                          <Text strong>Error: </Text>
                          {record.error}
                        </Text>
                      )}
                      {record.fileName && (
                        <Text style={{ fontSize: 12 }}>
                          <Text strong>File: </Text>
                          {record.fileName}
                        </Text>
                      )}
                    </Space>
                  </div>
                ),
                rowExpandable: (record) =>
                  !!(record.invoiceIds?.length || record.error || record.fileName),
              }}
            />
          </div>
        </Col>
      </Row>
    </div>
  )
}
